/**
 * Types for the generic scheduled-job API, plus a registry of the job types Data Hub can author.
 *
 * The backend exposes **one** set of endpoints for every job type: `job_type` is a field on the
 * definition rather than part of the path, and `payload` is an untyped JSON object whose shape only
 * that job type knows. So `ScheduledJob` mirrors the wire row exactly, and everything type-specific
 * lives in `JOB_TYPE_DESCRIPTORS` below.
 *
 * That registry is the extension point: adding a job type is one entry plus its payload type and
 * guard, and no change to the tab, the list, the slice or the hook. Note in particular that
 * `matchesDomain` is part of a descriptor rather than a shared helper — "which domain does this job
 * belong to" is itself a per-type payload convention (`payload.domain_id` happens to be
 * classification's), so each type has to declare it.
 */

import { JOB_TYPE } from "@/constants";

/** Values `background_jobs.status` takes. A run is finished once it is succeeded or failed. */
export const SCHEDULED_JOB_RUN_STATUS = {
  QUEUED: "queued",
  RUNNING: "running",
  SUCCEEDED: "succeeded",
  FAILED: "failed",
} as const;

export type ScheduledJobRunStatus =
  (typeof SCHEDULED_JOB_RUN_STATUS)[keyof typeof SCHEDULED_JOB_RUN_STATUS];

/** One `scheduled_jobs` row, exactly as the API returns it. */
export type ScheduledJob = {
  id: number;
  name: string;
  description: string | null;
  job_type: string;
  payload: Record<string, unknown>;
  /**
   * A five-field cron expression, UTC. Present on every row because the column is NOT NULL, and
   * edited through `JobScheduleFields` — presets over the common shapes, with the raw expression
   * one dropdown entry away. It says only *when* the job would run: `is_enabled` is what decides
   * whether cron fires it at all, so a job carries a cadence from the moment it is created and
   * runs on it only once somebody turns the switch on.
   */
  schedule: string;
  is_enabled: boolean;
  last_occurrence_at: string | null;
  created_at: string;
  updated_at: string;
};

/** One past firing, from `background_jobs`. Covers manual and scheduled runs alike. */
export type ScheduledJobRun = {
  job_id: string;
  status: string;
  created_at: string;
  started_at: string | null;
  finished_at: string | null;
  error: string | null;
};

/** Rows from a query preview, or `error` explaining why there are none. */
export type DomainQueryPreview = {
  columns: string[];
  rows: Record<string, unknown>[];
  row_count: number;
  truncated: boolean;
  error: string | null;
};

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

export function isScheduledJob(value: unknown): value is ScheduledJob {
  if (!isRecord(value)) {
    return false;
  }
  return (
    typeof value.id === "number" &&
    typeof value.name === "string" &&
    (value.description === null || typeof value.description === "string") &&
    typeof value.job_type === "string" &&
    isRecord(value.payload) &&
    typeof value.schedule === "string" &&
    typeof value.is_enabled === "boolean" &&
    (value.last_occurrence_at === null || typeof value.last_occurrence_at === "string") &&
    typeof value.created_at === "string" &&
    typeof value.updated_at === "string"
  );
}

export function isScheduledJobRun(value: unknown): value is ScheduledJobRun {
  if (!isRecord(value)) {
    return false;
  }
  return (
    typeof value.job_id === "string" &&
    typeof value.status === "string" &&
    typeof value.created_at === "string" &&
    (value.started_at === null || typeof value.started_at === "string") &&
    (value.finished_at === null || typeof value.finished_at === "string") &&
    (value.error === null || typeof value.error === "string")
  );
}

export function isDomainQueryPreview(value: unknown): value is DomainQueryPreview {
  if (!isRecord(value)) {
    return false;
  }
  return (
    Array.isArray(value.columns) &&
    value.columns.every((column) => typeof column === "string") &&
    Array.isArray(value.rows) &&
    value.rows.every(isRecord) &&
    typeof value.row_count === "number" &&
    typeof value.truncated === "boolean" &&
    (value.error === null || typeof value.error === "string")
  );
}

// ─── Classification job ──────────────────────────────────────────────

/** One query result column, and the label the model (and the example files) know it by. */
export type ClassificationInputColumn = {
  /** A column of the query's result. */
  column: string;
  /** What the model sees for this part, and the key read from uploaded labelled example files. */
  name: string;
};

/** Include a text field only when an *earlier* field's value is shorter than `shorter_than`. */
export type ClassificationIncludeWhen = {
  column: string;
  /** 1 means "only when that field is missing". */
  shorter_than: number;
};

/** One part of the text a record is classified on. Order in the list is order of importance. */
export type ClassificationTextField = ClassificationInputColumn & {
  /** Summarize first with one model call when longer than the embedding limit. */
  condense: boolean;
  include_when: ClassificationIncludeWhen | null;
};

/** Which query result columns a classification job reads, and how. */
export type ClassificationJobInput = {
  /** Unique per record: the output's id key, and the column the batching placeholder reads. */
  id: ClassificationInputColumn;
  /** Groups labelled examples for retrieval and is copied into the output. */
  product: ClassificationInputColumn | null;
  text_fields: ClassificationTextField[];
  /** Existing labels, level 1 first. At most three. */
  label_columns: string[];
  /** Appended to the classifier prompt. */
  instructions: string | null;
};

/** What a classification job's `payload` holds. Mirrors `ClassificationJobPayload` on the backend. */
export type ClassificationJobPayload = {
  domain_id: number;
  query: string;
  /**
   * Reads the taxonomy from a table on the same connection instead of the uploaded file. Absent or
   * null keeps the upload. A job with one cannot name review widgets yet — the backend refuses it.
   */
  taxonomy_query?: string | null;
  /**
   * A folder under the output connection's prefix where proposed categories are written as
   * `PROPOSED` rows for the taxonomy table; each case then carries its row's id as
   * `METADATA.PROPOSED_FANNEED_ID`. Needs `taxonomy_query`. Absent or null writes no rows.
   */
  proposed_taxonomy_path?: string | null;
  output_connection_id: number;
  /** A folder under the connection's own prefix. Empty (or absent) means the prefix itself. */
  output_path?: string;
  top_k?: number;
  /** Null switches proposals off. */
  invention_threshold?: number | null;
  review_threshold?: number;
  /**
   * Required on create, but absent on every definition written before jobs became generic — which
   * is why the guard below must not require it: those rows still have to list and delete.
   */
  input?: ClassificationJobInput;
  /**
   * The approval table widget whose approved rows this job also delivers, or absent when it
   * delivers classified rows alone.
   *
   * Optional and always both-or-neither — the backend rejects half a reference, since a widget is
   * addressed as `(app_id, table_id)` everywhere. Absent on every definition written before the
   * feature existed, which is why the guard below must not require them.
   */
  approval_app_id?: number | null;
  approval_table_id?: number | null;
  /**
   * The table widget where cases carrying a *proposed* category are reviewed first, or absent when
   * this job has no proposal stage. Whatever it does not settle falls through to the approval
   * widget above.
   *
   * Same both-or-neither rule as the approval pair, and the backend additionally refuses one
   * widget named for both roles — a widget cannot be both stages of its own review.
   */
  proposal_app_id?: number | null;
  proposal_table_id?: number | null;
};

/**
 * Whether a row's `payload` is a classification job's.
 *
 * Guarded rather than cast because `payload` really is untyped on the wire — the same endpoint
 * returns copy-job and email rows, whose payloads share none of these fields.
 */
export function isClassificationJobPayload(
  payload: Record<string, unknown>,
): payload is ClassificationJobPayload & Record<string, unknown> {
  return (
    typeof payload.domain_id === "number" &&
    typeof payload.query === "string" &&
    typeof payload.output_connection_id === "number"
  );
}

/** A classification job, narrowed so a renderer can read its payload without casting. */
export type ClassificationJob = ScheduledJob & {
  payload: ClassificationJobPayload & Record<string, unknown>;
};

/** Narrow a row to a classification job, or `null` when it is not one. */
export function asClassificationJob(job: ScheduledJob): ClassificationJob | null {
  if (job.job_type !== JOB_TYPE.CLASSIFICATION || !isClassificationJobPayload(job.payload)) {
    return null;
  }
  return job as ClassificationJob;
}

// ─── Copy job ────────────────────────────────────────────────────────

/**
 * Where a copy job writes.
 *
 * Two shapes reach the UI. `s3_connection` is the only one this form authors: the bucket comes off
 * a registered, verified S3 connection, so a job cannot be pointed at a bucket nobody proved was
 * writable. The raw `s3` shape predates it and is written by `scripts/copy_job.py` — read-only
 * here, and narrowed apart by `isCopyConnectionDestination` before the form will load a row.
 */
export type CopyConnectionDestination = {
  type: "s3_connection";
  connection_id: number;
  /** Relative to the connection's own configured prefix. Empty means the prefix itself. */
  folder: string;
};

/** A destination this UI can describe but not author. Fields are optional: it is somebody else's. */
export type CopyLegacyDestination = {
  type: string;
  bucket?: string;
  folder?: string;
};

export type CopyJobDestination = CopyConnectionDestination | CopyLegacyDestination;

/**
 * A copy job's selection of source documents.
 *
 * Paths are relative to `{domain_id}/` in the tenant's own bucket — one level above `inputs/`, so a
 * selection can reach both that and an app's `{app_id}/{table_id}/outputs/`. `folders` are re-listed
 * recursively on every run, so files added under one later are picked up with no config change.
 */
export type CopyJobSource = {
  folders: string[];
  files: string[];
};

/** What a copy job's `payload` holds. Mirrors `CopyJobPayload` on the backend. */
export type CopyJobPayload = {
  domain_id: number;
  source: CopyJobSource;
  destination: CopyJobDestination;
  /**
   * Delete the source objects once the copy has succeeded. Optional on the wire — absent on a
   * definition written before the field existed, and the backend defaults it to false.
   */
  delete_source_after?: boolean;
  /**
   * Copy only this many source files per run, in name order, starting after the last file a
   * previous run copied. Absent or null copies the whole selection every run.
   */
  files_per_run?: number | null;
};

/**
 * Whether a row's `payload` is a copy job's.
 *
 * Deliberately loose on `source` and `destination`: it checks they are the right *kind* of thing
 * rather than fully validating them, because a legacy row must still be listed and described. The
 * form applies the stricter `isCopyConnectionDestination` on top before it will edit one.
 */
export function isCopyJobPayload(
  payload: Record<string, unknown>,
): payload is CopyJobPayload & Record<string, unknown> {
  if (typeof payload.domain_id !== "number" || !isRecord(payload.destination)) {
    return false;
  }
  const source = payload.source;
  return isRecord(source) && (Array.isArray(source.folders) || Array.isArray(source.files));
}

/** A copy job, narrowed so a renderer can read its payload without casting. */
export type CopyJob = ScheduledJob & {
  payload: CopyJobPayload & Record<string, unknown>;
};

/** Narrow a row to a copy job, or `null` when it is not one. */
export function asCopyJob(job: ScheduledJob): CopyJob | null {
  if (job.job_type !== JOB_TYPE.COPY || !isCopyJobPayload(job.payload)) {
    return null;
  }
  return job as CopyJob;
}

/**
 * Whether this copy job's destination is one the form can author.
 *
 * The gate on editing. A raw-bucket destination is describable but not editable here, and loading
 * one into a connection-only form would mean saving it back as something it never was.
 */
export function isCopyConnectionDestination(
  destination: CopyJobDestination,
): destination is CopyConnectionDestination {
  return (
    destination.type === "s3_connection" &&
    typeof (destination as CopyConnectionDestination).connection_id === "number"
  );
}

/** The source paths of a copy job, folders first, as one flat list for display. */
export function copySourcePaths(payload: CopyJobPayload): string[] {
  return [...(payload.source.folders ?? []), ...(payload.source.files ?? [])];
}

// ─── App snapshot job ────────────────────────────────────────────────

/**
 * Which date the generated half of a snapshot name carries. Mirrors `SnapshotNameFormat`.
 *
 * The generated half is *only* a date — any wording around it (`Week`, `Day`, a report name) lives
 * in `name_prefix`, which the author types. The two are joined by a single space.
 */
export const SNAPSHOT_NAME_FORMAT = {
  /** ISO week and ISO week-year, e.g. `4 - 2026`. */
  WEEK_YEAR: "week_year",
  /** Day of month and calendar year, e.g. `16 - 2026`. */
  DAY_YEAR: "day_year",
  /** ISO-8601 calendar date, e.g. `2026-09-16`. */
  ISO_DATE: "iso_date",
} as const;

export type SnapshotNameFormatValue =
  (typeof SNAPSHOT_NAME_FORMAT)[keyof typeof SNAPSHOT_NAME_FORMAT];

/** What an app snapshot job's `payload` holds. Mirrors `AppSnapshotJobPayload` on the backend. */
export type AppSnapshotJobPayload = {
  /**
   * Stored beside `app_id` rather than derived from it: this per-domain tab places a row by
   * reading its payload, so a definition naming only an app would never appear here.
   */
  domain_id: number;
  app_id: number;
  /** The static half of the snapshot name, including any label word, e.g. `WBR - Week`. */
  name_prefix: string;
  name_format: SnapshotNameFormatValue;
};

/**
 * Whether a row's `payload` is an app snapshot job's.
 *
 * `name_format` is checked only for being a string rather than against the known values: a backend
 * that has shipped a second format must not make every row of this type unreadable to a UI that
 * has not. The renderer falls back rather than the guard failing.
 */
export function isAppSnapshotJobPayload(
  payload: Record<string, unknown>,
): payload is AppSnapshotJobPayload & Record<string, unknown> {
  return (
    typeof payload.domain_id === "number" &&
    typeof payload.app_id === "number" &&
    typeof payload.name_prefix === "string" &&
    typeof payload.name_format === "string"
  );
}

/** An app snapshot job, narrowed so a renderer can read its payload without casting. */
export type AppSnapshotJob = ScheduledJob & {
  payload: AppSnapshotJobPayload & Record<string, unknown>;
};

/** Narrow a row to an app snapshot job, or `null` when it is not one. */
export function asAppSnapshotJob(job: ScheduledJob): AppSnapshotJob | null {
  if (job.job_type !== JOB_TYPE.APP_SNAPSHOT || !isAppSnapshotJobPayload(job.payload)) {
    return null;
  }
  return job as AppSnapshotJob;
}

// ─── Registry ────────────────────────────────────────────────────────

export type JobTypeDescriptor = {
  /** The wire value stored in `scheduled_jobs.job_type`. */
  jobType: string;
  /** i18n key under `domainDetail.jobs.types` for this type's display name. */
  labelKey: string;
  /** Whether a row of this type belongs to `domainId`. Per-type: see the module doc. */
  matchesDomain: (job: ScheduledJob, domainId: number) => boolean;
  /**
   * Whether `JobFormModal` can edit a stored row of this type.
   *
   * Per-type rather than assumed, because it is not universal. A copy job is editable only when its
   * destination is a connection — `isEditableJob` adds that check per row.
   */
  canEdit: boolean;
};

export const JOB_TYPE_DESCRIPTORS: readonly JobTypeDescriptor[] = [
  // Order matters: `JobFormModal` preselects the first entry a tenant is entitled to, so
  // classification stays first and new types are appended.
  {
    jobType: JOB_TYPE.CLASSIFICATION,
    labelKey: "classification.label",
    matchesDomain: (job, domainId) => asClassificationJob(job)?.payload.domain_id === domainId,
    canEdit: true,
  },
  {
    jobType: JOB_TYPE.COPY,
    labelKey: "copy.label",
    matchesDomain: (job, domainId) => asCopyJob(job)?.payload.domain_id === domainId,
    canEdit: true,
  },
  {
    jobType: JOB_TYPE.APP_SNAPSHOT,
    labelKey: "appSnapshot.label",
    matchesDomain: (job, domainId) => asAppSnapshotJob(job)?.payload.domain_id === domainId,
    canEdit: true,
  },
];

/** Whether the form can edit this row, for the list's Edit control. */
export function isEditableJob(job: ScheduledJob): boolean {
  if (!(descriptorFor(job.job_type)?.canEdit ?? false)) {
    return false;
  }
  // A copy job written by `scripts/copy_job.py` with a raw bucket or warehouse destination cannot
  // be loaded into a connection-only form without being saved back as something it never was.
  const copyJob = asCopyJob(job);
  return copyJob === null || isCopyConnectionDestination(copyJob.payload.destination);
}

export function descriptorFor(jobType: string): JobTypeDescriptor | null {
  return JOB_TYPE_DESCRIPTORS.find((descriptor) => descriptor.jobType === jobType) ?? null;
}

/**
 * Whether `job` is one this domain's Jobs tab should list.
 *
 * A row of a type this UI does not know about is excluded rather than shown untyped: the list has
 * no way to render its payload, and `GET /scheduled-jobs` is tenant-wide across every job type.
 */
export function isJobForDomain(job: ScheduledJob, domainId: number): boolean {
  return descriptorFor(job.job_type)?.matchesDomain(job, domainId) ?? false;
}
